import {ItemPicker} from '@github-ui/item-picker-base/ItemPicker'
import {LockIcon, RepoIcon, RepoLockedIcon, TriangleDownIcon} from '@primer/octicons-react'
import {Breadcrumbs, IconButton} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useMemo, useState} from 'react'

import type {ContextCrumb} from '../../types'
import {useGlobalNavAnalytics} from '../../utils/Analytics'
import styles from './styles.module.css'
import {type Repository, useRepositorySuggestions} from './use-repository-suggestions'

export interface RepoPickerProps {
  repositoryCrumb: ContextCrumb
  ownerCrumb: ContextCrumb
  onCrumbClick?: () => void
}

export function RepoPicker({repositoryCrumb, ownerCrumb, onCrumbClick}: RepoPickerProps) {
  const [isOpen, setIsOpen] = useState(false)
  const {sendItemClick} = useGlobalNavAnalytics('REPO_PICKER')
  const isCurrentRepoPrivate = repositoryCrumb.octicon === 'lock'

  const {repositories, isLoading, searchQuery, handleFilterChange} = useRepositorySuggestions({
    owner: ownerCrumb.label,
    enabled: isOpen,
    currentRepositoryName: repositoryCrumb.label,
    currentRepositoryVisibility: isCurrentRepoPrivate ? 'private' : 'public',
  })

  const convertToItemProps = useCallback((repo: Repository) => {
    const isPrivate = repo.visibility === 'private' || repo.visibility === 'internal'

    return {
      id: repo.nameWithOwner,
      text: repo.name,
      source: repo,
      leadingVisual: () => (isPrivate ? <RepoLockedIcon size={16} /> : <RepoIcon size={16} />),
    }
  }, [])

  const getItemKey = useCallback((repo: Repository) => repo.nameWithOwner, [])

  const handleSelectionChange = useCallback(
    (items: Repository[]) => {
      const selectedRepo = items[0]
      if (selectedRepo) {
        sendItemClick('REPO_SELECTED')
        window.location.href = `/${selectedRepo.nameWithOwner}`
      }
    },
    [sendItemClick],
  )

  const currentRepoHref = repositoryCrumb.href || repositoryCrumb.path || `/${repositoryCrumb.label}`
  // TODO: handle private forked repo
  const currentRepoName = repositoryCrumb.label.split('/').pop() || repositoryCrumb.label

  const initialSelectedItems = useMemo(() => {
    const currentRepo = repositories.find(repo => repo.name === repositoryCrumb.label)
    return currentRepo ? [currentRepo] : []
  }, [repositories, repositoryCrumb.label])

  return (
    <>
      <Breadcrumbs.Item
        as="a"
        href={currentRepoHref}
        className={clsx(styles.contextCrumb, styles.contextCrumbLast, styles.repoPickerCrumb)}
        onClick={onCrumbClick}
      >
        {isCurrentRepoPrivate ? <LockIcon className={styles.lockIcon} /> : null}
        {currentRepoName}
      </Breadcrumbs.Item>
      <ItemPicker<Repository>
        renderAnchor={({'aria-labelledby': ariaLabelledBy, 'aria-label': ariaLabel, ...anchorProps}) => (
          <IconButton
            {...anchorProps}
            icon={TriangleDownIcon}
            variant="invisible"
            size="small"
            aria-label="Switch repository"
            className={clsx(styles.contextCrumb, styles.repoPickerDropdownButton)}
          />
        )}
        items={repositories}
        initialSelectedItems={initialSelectedItems}
        placeholderText="Search repositories"
        selectionVariant="single"
        loading={isLoading}
        filterItems={handleFilterChange}
        getItemKey={getItemKey}
        convertToItemProps={convertToItemProps}
        onSelectionChange={handleSelectionChange}
        onOpen={() => {
          sendItemClick('REPO_PICKER_TOGGLE')
          setIsOpen(true)
        }}
        onClose={() => setIsOpen(false)}
        title="Switch repository"
        width="medium"
        improvedNoMatchAccessibility
        noMatchMessage={
          isLoading
            ? {variant: 'empty' as const, title: 'Loading repositories...', body: ''}
            : repositories.length === 0 && searchQuery
              ? {variant: 'empty' as const, title: 'No repositories found', body: ''}
              : undefined
        }
      />
    </>
  )
}

try{ RepoPicker.displayName ||= 'RepoPicker' } catch {}